import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Trash2, Shield } from 'lucide-react';
import { useMembers, usePendingInvites, useSendInvite, useRevokeInvite, useUpdateMemberRole, useRemoveMember } from '../../hooks/useMembers.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import { toast } from '../../lib/toast.js';
import type { UserRole } from '@mayksel/types';

const ROLES: UserRole[] = ['admin', 'member', 'viewer'];

const ROLE_COLORS: Record<UserRole, string> = {
  owner: 'var(--color-brand)',
  admin: 'var(--color-info)',
  member: 'var(--color-success)',
  viewer: 'var(--color-text-muted)',
};

export default function MembersSettings() {
  const { t } = useTranslation('settings');
  const membersEnabled = useFeatureFlag('members');
  const currentUser = useWorkspaceStore((s) => s.user);

  const { data: members = [], isLoading: membersLoading } = useMembers();
  const { data: invites = [], isLoading: invitesLoading } = usePendingInvites();
  const sendInvite = useSendInvite();
  const revokeInvite = useRevokeInvite();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('member');

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      await sendInvite.mutateAsync({ email: inviteEmail.trim(), role: inviteRole });
      toast.success(t('members.invite_sent', { email: inviteEmail }));
      setInviteEmail('');
      setInviteRole('member');
    } catch {
      toast.error(t('members.invite_error'));
    }
  }

  async function handleRevoke(inviteId: string, email: string) {
    if (!confirm(t('members.revoke_confirm', { email }))) return;
    try {
      await revokeInvite.mutateAsync(inviteId);
    } catch {
      toast.error(t('members.revoke_error'));
    }
  }

  async function handleRemove(userId: string, name: string) {
    if (!confirm(t('members.remove_confirm', { name }))) return;
    try {
      await removeMember.mutateAsync(userId);
    } catch {
      toast.error(t('members.remove_error'));
    }
  }

  async function handleRoleChange(userId: string, role: UserRole) {
    try {
      await updateRole.mutateAsync({ userId, role });
    } catch {
      toast.error(t('members.remove_error'));
    }
  }

  if (!membersEnabled) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        Member management is not enabled for your account.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('members.title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('members.subtitle')}
        </p>
      </div>

      {/* Invite form */}
      <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-4)' }}>
          {t('members.invite_button')}
        </p>
        <form onSubmit={(e) => void handleInvite(e)} style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <input
            type="email"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder={t('members.invite_email_label')}
            style={{
              flex: 1, minWidth: 200,
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
            }}
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as UserRole)}
            aria-label={t('members.invite_role_label')}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
            }}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{t(`members.role_${r}`)}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={sendInvite.isPending}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
              border: 'none', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', cursor: 'pointer',
              opacity: sendInvite.isPending ? 0.7 : 1,
            }}
          >
            <UserPlus size={14} />
            {sendInvite.isPending ? t('members.invite_sending') : t('members.invite_send')}
          </button>
        </form>
      </div>

      {/* Members list */}
      <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 'var(--space-5)' }}>
        <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle)' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('members.status_active')} ({members.length})
          </p>
        </div>
        {membersLoading ? (
          <div style={{ padding: 'var(--space-4)' }}>
            {[1, 2].map((i) => <div key={i} className="skeleton-line" style={{ height: 44, marginBottom: 8, borderRadius: 'var(--radius-md)' }} />)}
          </div>
        ) : members.length === 0 ? (
          <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            {t('members.no_members')}
          </div>
        ) : (
          members.map((member, idx) => (
            <div
              key={member.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: idx < members.length - 1 ? '1px solid var(--color-border)' : 'none',
              }}
            >
              {/* Avatar */}
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--color-brand-pale)', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-brand)',
                overflow: 'hidden',
              }}>
                {member.avatarUrl
                  ? <img src={member.avatarUrl} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : member.name.charAt(0).toUpperCase()
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                  {member.name}
                  {member.id === currentUser?.id && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginLeft: 6 }}>(you)</span>
                  )}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{member.email}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                {member.role === 'owner' ? (
                  <span style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 'var(--text-xs)', color: ROLE_COLORS.owner,
                    fontWeight: 'var(--font-medium)',
                  }}>
                    <Shield size={12} /> {t('members.role_owner')}
                  </span>
                ) : (
                  <select
                    value={member.role}
                    onChange={(e) => void handleRoleChange(member.id, e.target.value as UserRole)}
                    disabled={member.id === currentUser?.id}
                    style={{
                      padding: '2px var(--space-2)',
                      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-xs)', color: ROLE_COLORS[member.role],
                      background: 'var(--color-bg)', cursor: 'pointer',
                    }}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{t(`members.role_${r}`)}</option>
                    ))}
                  </select>
                )}
                {member.role !== 'owner' && member.id !== currentUser?.id && (
                  <button
                    onClick={() => void handleRemove(member.id, member.name)}
                    aria-label={`Remove ${member.name}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: 4 }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pending invites */}
      {(invites.length > 0 || invitesLoading) && (
        <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-subtle)' }}>
            <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('members.status_pending')} ({invites.length})
            </p>
          </div>
          {invitesLoading ? (
            <div style={{ padding: 'var(--space-4)' }}>
              <div className="skeleton-line" style={{ height: 44, borderRadius: 'var(--radius-md)' }} />
            </div>
          ) : (
            invites.map((invite, idx) => (
              <div
                key={invite.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  borderBottom: idx < invites.length - 1 ? '1px solid var(--color-border)' : 'none',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--color-bg-muted)', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
                }}>
                  ?
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{invite.email}</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {t(`members.role_${invite.role}`)} · {t('members.status_pending')}
                  </p>
                </div>
                <button
                  onClick={() => void handleRevoke(invite.id, invite.email)}
                  aria-label={`Revoke invite for ${invite.email}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: 4 }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
